type FlowVisibilityGlobalState = {
  companyId: string
  headCompanyId: string | null
  company: { inheritFlows?: boolean }
}

/** Company whose flow visibility settings apply — head company when sub-companies inherit flows. */
export function getCompanyIdForFlowVisibility(globalState: FlowVisibilityGlobalState) {
  return globalState.headCompanyId && globalState.company.inheritFlows
    ? globalState.headCompanyId
    : globalState.companyId
}

/**
 * Which companies can see a flow. Matches the flow details tab, which infers the mode from
 * the lists and does not read `isPrivate` (that flag can drift from the lists on save).
 *
 * - Allow lists have any companies or tags: only those companies.
 * - Otherwise: every company except those on the block lists.
 * - No lists: every company.
 *
 * Allow lists win when both are populated, same as the details tab.
 */
export const flowVisibilitySettingsFilter = (companyId: string, pathToFlow: string[] = []) => ({
  or: [
    {
      and: [
        {
          or: [
            { not: { empty: { path: [...pathToFlow, "allowedCompanies"] } } },
            { not: { empty: { path: [...pathToFlow, "allowedCompanyTags"] } } },
          ],
        },
        {
          or: [
            {
              exists: {
                path: [
                  ...pathToFlow,
                  { name: "allowedCompanies", filter: { matches: { id: companyId } } },
                ],
              },
            },
            {
              exists: {
                path: [
                  ...pathToFlow,
                  {
                    name: "allowedCompanyTags",
                    filter: { matches: { companies: { some: { id: companyId } } } },
                  },
                ],
              },
            },
          ],
        },
      ],
    },
    {
      and: [
        { empty: { path: [...pathToFlow, "allowedCompanies"] } },
        { empty: { path: [...pathToFlow, "allowedCompanyTags"] } },
        {
          empty: {
            path: [
              ...pathToFlow,
              { name: "blockedCompanies", filter: { matches: { id: companyId } } },
            ],
          },
        },
        {
          empty: {
            path: [
              ...pathToFlow,
              {
                name: "blockedCompanyTags",
                filter: { matches: { companies: { some: { id: companyId } } } },
              },
            ],
          },
        },
      ],
    },
  ],
})

// This filter can be run against the flows model to determine which flows
// should be visible to the currently logged in user.
export const companyFlowVisibilitySettingsFilter = (
  companyId: string,
  pathToFlow: string[] = [],
) => ({
  or: [
    // No visibility is set
    {
      empty: {
        path: [
          ...pathToFlow,
          {
            name: "companyFlowVisibilitySettings",
            filter: { matches: { company: companyId } },
          },
        ],
      },
    },
    // visibility is set to visible and we are not in the exception list
    {
      exists: {
        path: [
          ...pathToFlow,
          {
            name: "companyFlowVisibilitySettings",
            filter: {
              matches: {
                company: companyId,
                visibility: "visible",
                users: { none: { id: { session: true } } },
              },
            },
          },
        ],
      },
    },
    // visibility is set to hidden and we are in the exception list
    {
      exists: {
        path: [
          ...pathToFlow,
          {
            name: "companyFlowVisibilitySettings",
            filter: {
              matches: {
                company: companyId,
                visibility: "hidden",
                users: { some: { id: { session: true } } },
              },
            },
          },
        ],
      },
    },
  ],
})
